<template>
  <div :class="$style.reports">
    <reports-title :title="pageTitle" />
    <reports-toolbar
      :report-title="pageTitle"
      :toolbar-disabled="!projects.length"
      @onExportPortfolioReport="onExportPortfolioReport"
    />
    <tanstack-table
      id="report_export_id"
      ref="tableRef"
      :is-full-height="true"
      :config="tableConfig"
      @contextMenu="contextMenu"
      @sortingChanged="sortingChanged"
    />
    <div
      v-if="isShowStub"
      :class="$style.stub_with_image"
    >
      <div :class="$style.wrapper">
        <vgp-svg-image
          name="comment-hub-no-search"
        />
        <div :class="$style.desc">
          {{ locale('report_page_no_filtered_data') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import reportsTitle from '../include/title.vue';
import reportsToolbar from '../include/toolbar.vue';
import tanstackTable from '$$vueCmp/table/table.vue';
import moment from '../../../libs/moment';
import multiViewsProjects from '../../../models/multiViewsProjects';
import projects from '../../../models/projects';
import rights from '../../../components/rights/index';
import projectComponent from '../../../components/Project';
import vgpSvgImage from '$$vueCmp/VueIcon/vgpSvgImage.vue';
import Export from '../include/export';
import reportsConstants from '../include/constants';
import projectsModel from '../../../models/projects';

export default {
  components: {
    reportsTitle, tanstackTable, reportsToolbar, vgpSvgImage,
  },
  props: {

  },
  data() {
    return {
      type: undefined,
      locale: __,
      projects: [],
      isShowStub: false,
    };
  },
  computed: {
    pageTitle() {
      return reportsConstants.TITLE.progress.projectsList;
    },
    currentFilter() {
      return this.$store.getters['filter/currentFilter'];
    },
    tableConfig() {
      return {
        columns: [
          {
            id: 'name',
            header: this.locale('gantt_grid_project_name'),
            type: 'text',
            showTooltip: true,
            tooltipContent: row => row.original.name,
            enableResizing: false,
          },
          {
            id: 'status',
            header: this.locale('gantt_grid_status'),
            type: 'status',
            size: 170,
            sortingFn: (rowA, rowB, columnId) => {
              const aValue = rowA.getValue(columnId);
              const bValue = rowB.getValue(columnId);

              return aValue === bValue ? 0 : aValue > bValue ? 1 : -1;
            },
            accessorFn: row => row.status.label,
          },
          {
            id: 'progress',
            header: this.locale('gantt_grid_progress'),
            type: 'progress',
            size: 150,
            showTooltip: true,
            sortingFn: (rowA, rowB, columnId) => {
              const aValue = rowA.getValue(columnId).percentage;
              const bValue = rowB.getValue(columnId).percentage;

              return aValue === bValue ? 0 : aValue > bValue ? 1 : -1;
            },
            tooltipContent: row => {
              const {
                countOfPerSchedule,
                countOfSlightlyOverdue,
                countOfOverdue,
              } = row.original.progress;

              return `
                <div>${this.locale('project_countOfPerSchedule', { count: countOfPerSchedule })}</div>
                <div>${this.locale('project_countOfSlightlyOverdue', { count: countOfSlightlyOverdue })}</div>
                <div>${this.locale('project_countOfOverdue', { count: countOfOverdue })}</div>
              `;
            },
          },
          {
            id: 'start_date',
            header: this.locale('gantt_grid_start_date'),
            type: 'text',
            size: 130,
            sortingFn: (rowA, rowB, columnId) => {
              const aValue = moment(rowA.getValue(columnId), user.dateFormat);
              const bValue = moment(rowB.getValue(columnId), user.dateFormat);

              return aValue.diff(bValue, 'days') === 0 ? 0 : aValue.diff(bValue, 'days') > 0 ? 1 : -1;
            },
          },
          {
            id: 'end_date',
            header: this.locale('gantt_grid_end_date'),
            type: 'text',
            size: 130,
            sortingFn: (rowA, rowB, columnId) => {
              const aValue = moment(rowA.getValue(columnId), user.dateFormat);
              const bValue = moment(rowB.getValue(columnId), user.dateFormat);

              return aValue.diff(bValue, 'days') === 0 ? 0 : aValue.diff(bValue, 'days') > 0 ? 1 : -1;
            },
          },
          {
            id: 'creator',
            header: this.locale('project_owner'),
            type: 'person',
            size: 180,
            accessorFn: row => row.creator.name,
            showTooltip: true,
            tooltipContent: row => {
              const creator = row.original.creator;

              return creator.name ? `<div style="display: flex; align-items: center; gap: 4px;">
                ${creator.photo ? `<div style="
                  background-image: url(${creator.photo});
                  width: 20px;
                  height: 20px;
                  border-radius: 50%;
                  background-size: 100%;
                  flex-shrink: 0;
                  padding: 1.667px 2.333px 2.333px 1.667px;
                  "></div>` : ''}
                <div>${creator.name}</div>
                </div>` : '';
            },
          },
        ],
        data: this.projects,
        state: {
          columnPinning: {
            left: ['name'],
            right: ['_context_menu'],
          },
          sorting: [{
            id: 'start_date',
            desc: false,
          }],
        },
      };
    },
  },
  watch: {
    'currentFilter.properties': function () {
      this.init();
    },
    projects(data) {
      if (!data?.length) {
        this.isShowStub = true;
      } else {
        this.isShowStub = false;
      }
    },
  },
  created() {
    this.init();
  },
  mounted() {
    const route = this.$route;
    const reportNameAnalytics = this.pageTitle.replace(/^exmp_chart_/, '').replace(/_title$/, '');

    userExtAnalytics.log('report_open', {
      report_type: reportNameAnalytics,
      source_type: route?.params?.sourceType || null,
      projects_selected_count: this.projects.length,
    });
  },
  methods: {
    init() {
      let projectsIds = [];
      const filterProjectIds = this.currentFilter?.properties?.gantt_id;
      const filterPortfolioIds = this.currentFilter?.properties?.portfolio_id;

      if (filterProjectIds?.length) {
        projectsIds = [...this.currentFilter.properties.gantt_id];
      }

      if (filterPortfolioIds?.length) {
        const multiviewData = multiViewsProjects.getProjectDataById(this.currentFilter.properties.portfolio_id[0]);
        const multivewProjectIds = multiviewData?.ganttIDs ? [...multiviewData.ganttIDs] : [];

        projectsIds = multivewProjectIds;
      }

      if (!projectsIds.length) {
        projectsIds = projectsModel.getAllProjects().map(project => project.gantt_id);
      }

      this.projects = projectsIds.map(ganttID => {
        const { id, name } = projects.getProjectDataById(ganttID);

        const status = this.getStatus(ganttID);
        const progress = this.getProgress(ganttID);
        const start_date = this.getStartDate(ganttID);
        const end_date = this.getEndDate(ganttID);
        const creator = this.getCreator(ganttID);

        return {
          id, name, status, progress, start_date, end_date, creator,
        };
      }).filter(item => {
        if (this.currentFilter?.properties?.owner_id?.length) {
          return this.currentFilter.properties.owner_id.includes(item.creator.id);
        }

        return item;
      }).filter(item => {
        if (this.currentFilter?.properties?.project_status?.length) {
          return this.currentFilter.properties.project_status.includes(item.status.id);
        }

        return item;
      });
    },
    getProgress(ganttID) {
      return {
        percentage: Math.floor(this.getTotalEstimate(ganttID).progress * 100),
        ...this.getTasksCount(ganttID),
      };
    },
    getStatus(ganttID) {
      const projectStatusesLocales = ['no_status_locale', 'on_track_locale', 'at_risk_locale', 'off_track_locale'];
      const statusData = this.$store.getters['teamSetting/statusDataForProject'](ganttID);
      const {
        id,
        value: label,
        labelColor,
        background: backgroundColor,
      } = statusData.selectOption;

      return {
        id,
        label: projectStatusesLocales.includes(label) ? __(label) : label,
        labelColor,
        backgroundColor,
      };
    },
    getTotalEstimate(ganttID) {
      return this.$store.getters['tasksModel/getTotalEstimateDataForProject'](ganttID);
    },
    getStartDate(ganttID) {
      return moment(this.getTotalEstimate(ganttID).start_date).format(user.dateFormat);
    },
    getEndDate(ganttID) {
      return moment(this.getTotalEstimate(ganttID).end_date).format(user.dateFormat);
    },
    getCreator(ganttID) {
      return rights.project.getOwnerByGanttId(ganttID) || { name: '', photo: null };
    },
    getTasksCount(ganttID) {
      const {
        countOfOverdue,
        countOfSlightlyOverdue,
        countOfPerSchedule,
      } = projectComponent.getProjectsList().find(item => item.id === ganttID);

      return {
        countOfOverdue,
        countOfSlightlyOverdue,
        countOfPerSchedule,
      };
    },
    sortingChanged(payload) {
      this.sortingState = payload;
    },
    onExportPortfolioReport() {
      const sortedData = this.$refs.tableRef?.tableInstance.getSortedRowModel().rows.map(row => row.original);
      const exportPayload = { ...this.tableConfig };

      if (sortedData) {
        exportPayload.data = sortedData;
      }

      Export.exportToExcel({
        report: 'portfolio',
        data: exportPayload,
        title: this.locale(this.pageTitle),
      });
    },
  },
};
</script>

<style module src='./../less/reportsMain.less'></style>
